import React from "react";
import { FormControl, InputLabel, Select, MenuItem, Switch, SelectChangeEvent } from "@mui/material";
import { styled } from "@mui/material/styles";
import MDTypography from "../../../../../components/MDTypography";
import MDBox from "../../../../../components/MDBox";
import colors from "assets/theme-dark/base/colors";
import { AnalysisDriverType } from "../../types/types";
import { Column } from "../../HeadRoom";
import { cruxyColors } from "../../theme/color-palette";

interface DriverSelectionProps {
  driverNumber: 1 | 2;
  driverType: string;
  numeratorType: string;
  driverMode: AnalysisDriverType;
  columns: Column[];
  handleDriverChange: (event: SelectChangeEvent<string>) => void;
  handleNumeratorChange: (event: SelectChangeEvent<string>) => void;
  handleDriverModeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  formatPercentageDisplay: (numerator: string, denominator: string) => string;
}

const DriverSelection: React.FC<DriverSelectionProps> = ({
  driverNumber,
  driverType,
  numeratorType,
  driverMode,
  columns,
  handleDriverChange,
  handleNumeratorChange,
  handleDriverModeChange,
  formatPercentageDisplay,
}) => {
  const numericColumns = columns?.filter((c: Column) => c.isNumeric === true)?.map((c: Column) => c.name) || [];
  const allColumns = columns?.map((c: Column) => c.name) || [];
  const availableColumns = driverMode === "PERCENTAGE" ? numericColumns : allColumns;
  const shouldDisableSwitch = !driverType && driverMode === "COLUMN";

  return (
    <StyledContainer mb={1.5}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <MDTypography variant="caption" fontWeight="bold">
          Driver {driverNumber}{" "}
          <MDTypography component="span" variant="caption" fontWeight="regular">
            <span style={{ color: colors.grey[600], fontSize: "0.7rem" }}>
              ({driverMode === "COLUMN" ? "Column" : "Percentage"})
            </span>
          </MDTypography>
        </MDTypography>
        <MDBox display="flex" alignItems="center">
          <MDTypography variant="caption">Column</MDTypography>
          <Switch
            size="small"
            checked={driverMode === "PERCENTAGE"}
            onChange={handleDriverModeChange}
            color="primary"
            disabled={shouldDisableSwitch}
          />
          <MDTypography variant="caption">Percentage</MDTypography>
        </MDBox>
      </MDBox>
      {driverMode === "PERCENTAGE" ? (
        <MDBox display="flex" gap={1}>
          <FormControl fullWidth size="small" variant="outlined" sx={{ flex: 2 }}>
            <InputLabel>Column to calculate</InputLabel>
            <Select
              value={numeratorType}
              onChange={handleNumeratorChange}
              label="Column to calculate"
              sx={{ "& .MuiSelect-select": { color: colors.primary.main, fontSize: "0.75rem" } }}
            >
              <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                <em>None</em>
              </MenuItem>
              {availableColumns?.map((c) => (
                <MenuItem key={c} value={c} sx={{ fontSize: "0.75rem" }}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" variant="outlined" sx={{ flex: 3 }}>
            <InputLabel>Percentage of</InputLabel>
            <Select
              value={driverType}
              onChange={handleDriverChange}
              label="Percentage of"
              sx={{ "& .MuiSelect-select": { color: colors.primary.main, fontSize: "0.75rem" } }}
            >
              <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                <em>None</em>
              </MenuItem>
              {availableColumns?.map((c) => (
                <MenuItem key={c} value={c} sx={{ fontSize: "0.75rem" }}>
                  {numeratorType ? formatPercentageDisplay(numeratorType, c) : c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
      ) : (
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel>Select driver {driverNumber}</InputLabel>
          <Select
            value={driverType}
            onChange={handleDriverChange}
            label={`Select driver ${driverNumber}`}
            sx={{ "& .MuiSelect-select": { color: colors.primary.main, fontSize: "0.75rem" } }}
          >
            <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
              <em>None</em>
            </MenuItem>
            {availableColumns?.map((ac) => (
              <MenuItem key={ac} value={ac} sx={{ fontSize: "0.75rem" }}>
                {ac}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled(MDBox)(({ theme }) => ({
  border: `1px solid ${cruxyColors.amber.main}`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[1],
  },
}));

export default DriverSelection;
